/**
 * ChaseTheAce V2 - Classic V1 Recreation sections for GridEngine
 * Faithful recreation with 3-column layout, dark sidebar, countdown boxes
 */
import React from 'react'
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import ResponsiveImage from '../../../components/ResponsiveImage'
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import HeroSiteLogo from '../../../components/HeroSiteLogo'
import HeroPartnerGlassSelect from '../../../components/HeroPartnerGlassSelect'
import { useContent } from '../../../contexts/ContentContext'
import { usePurchaseNavigate } from '../../../hooks/usePurchaseNavigate'
import { useCountdown } from '../../../hooks/useCountdown'
import { useCharityTheme } from '../../../contexts/CharityThemeContext'
import { useSiteConfig } from '../../../contexts/SiteConfigContext'
import { charityConfig } from '../../../config/charity'

/**
 * Hero section - V1 recreation: 3-column layout (decorative cards left / cream panel center / dark sidebar right)
 */
export function ChaseV2HeroSection({
  mainDraw,
  partner,
  onLoginClick,
  editMode,
  handleTranslationUpdate,
  salesClosed = false,
}) {
  const { c } = useContent()
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)
  const { branding } = useCharityTheme()
  const { siteDetails } = useSiteConfig() || {}
  const { timeLeft } = useCountdown(mainDraw, { enableAnimation: true })

  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 0)
  const weeklyPrize = Math.floor(mainDraw?.weekly_prize ?? mainDraw?.secondary_prize ?? 0)
  const actualJackpotAmount = Math.floor(mainDraw?.actual_jackpot_amount ?? 0)
  const actualWeeklyAmount = Math.floor(mainDraw?.actual_weekly_amount ?? 0)
  const showJackpotGuarantee = mainDraw?.show_jackpot_guarantee_text ?? false
  const showWeeklyGuarantee = mainDraw?.show_weekly_guarantee_text ?? false
  const showJackpotGuaranteeAmount = mainDraw?.show_jackpot_guarantee_amount ?? false
  const showWeeklyGuaranteeAmount = mainDraw?.show_weekly_guarantee_amount ?? false
  const currentGameNumber = mainDraw?.game_number ?? 1
  const currentWeekNumber = mainDraw?.week_number ?? 1

  const drawDateLabel = mainDraw?.draw_date
    ? new Date(mainDraw.draw_date).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : ''

  const brandLogo = siteDetails?.siteLogo || charityConfig.logoUrl
  const brandName = siteDetails?.siteName || charityConfig.name
  const defaultPlatformLogo = 'https://cms.tlffs.ca/uploads/header_Logo_4f095fb08b.svg'
  const isDefaultLogo = branding?.logoUrl === defaultPlatformLogo
  const logoUrl = isDefaultLogo ? brandLogo : branding?.logoUrl || brandLogo
  const logoAlt = isDefaultLogo
    ? brandName
    : branding?.charityName || brandName || 'Charity Partner'
  const orgWebsite = branding?.charityWebsite || charityConfig.website || '#'

  return (
    <section className="min-h-[65vh] flex items-center bg-black overflow-hidden relative">
      {/* Background pattern */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <HeroSiteLogo tone="dark" />
        <HeroPartnerGlassSelect className="mx-auto mb-6 w-full max-w-md" />
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-center">
          {/* LEFT: Decorative playing cards (hidden on mobile) */}
          <div
            className="hidden lg:flex items-center justify-center relative lg:col-span-3"
            style={{ minHeight: '400px' }}
          >
            {/* Back card (rotated) */}
            <div
              className="absolute w-48 h-72 bg-white rounded-xl shadow-2xl flex items-center justify-center"
              style={{
                transform: 'rotate(-15deg)',
                border: '3px solid var(--charity-accent)',
                left: '10%',
                top: '10%',
              }}
            >
              <span className="text-8xl text-gray-800">♠</span>
            </div>
            {/* Front card (slightly rotated) */}
            <div
              className="absolute w-48 h-72 bg-white rounded-xl shadow-2xl flex items-center justify-center"
              style={{
                transform: 'rotate(5deg)',
                border: '3px solid var(--charity-accent)',
                left: '25%',
                top: '5%',
              }}
            >
              <div className="text-center">
                <div className="text-sm font-bold text-gray-800 absolute top-3 left-3">A</div>
                <span className="text-8xl text-gray-800">♠</span>
                <div className="text-sm font-bold text-gray-800 absolute bottom-3 right-3 rotate-180">A</div>
              </div>
            </div>
          </div>

          {/* CENTER: White/cream info panel */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-md mx-auto text-center">
              {/* Game & Week Info */}
              <div className="mb-2 text-sm font-medium text-charity-accent">
                <InlineTranslationEdit
                  translationKey="chaseTheAce.game"
                  value={c('chaseTheAce.game')}
                  fallback="Game"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />{' '}
                {currentGameNumber} -{' '}
                <InlineTranslationEdit
                  translationKey="chaseTheAce.week"
                  value={c('chaseTheAce.week')}
                  fallback="Week"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />{' '}
                {currentWeekNumber}
              </div>

              {/* Draw Date */}
              {drawDateLabel && (
                <div className="mb-4 text-sm text-gray-500 italic">
                  <InlineTranslationEdit
                    translationKey="chaseTheAce.drawDate"
                    value={c('chaseTheAce.drawDate')}
                    fallback="Draw Date"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                  : {drawDateLabel}
                </div>
              )}

              {/* Countdown Timer - bordered boxes */}
              <div className="flex items-center justify-center gap-2 my-4">
                {[
                  { value: timeLeft.days, label: 'DAYS' },
                  { value: timeLeft.hours, label: 'HOURS' },
                  { value: timeLeft.minutes, label: 'MINUTES' },
                  { value: timeLeft.seconds, label: 'SECONDS' },
                ].map((unit, i) => (
                  <React.Fragment key={unit.label}>
                    {i > 0 && <span className="text-gray-400 font-bold">:</span>}
                    <div className="flex flex-col items-center">
                      <div className="border border-gray-300 rounded px-2 py-1.5 min-w-[40px] text-center">
                        <span className="text-xl font-bold text-gray-900">
                          {String(unit.value).padStart(2, '0')}
                        </span>
                      </div>
                      <span className="text-[9px] text-gray-500 uppercase tracking-wider mt-1">{unit.label}</span>
                    </div>
                  </React.Fragment>
                ))}
              </div>

              {/* Main Jackpot */}
              <div className="mb-2 mt-4">
                <div className="text-xs font-bold text-gray-900 uppercase tracking-widest mb-2">
                  <InlineTranslationEdit
                    translationKey="chaseTheAce.jackpotTitle"
                    value={c('chaseTheAce.jackpotTitle')}
                    fallback="Chase The Ace Jackpot"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </div>
                <div className="text-7xl md:text-8xl font-black text-gray-900">
                  ${jackpotAmount.toLocaleString()}
                </div>
                {showJackpotGuarantee && (
                  <div className="text-xs text-gray-500 italic mt-2">
                    <InlineTranslationEdit
                      translationKey="chaseTheAce.guaranteeText"
                      value={c('chaseTheAce.guaranteeText')}
                      fallback="Guaranteed Prize"
                      onUpdate={handleTranslationUpdate}
                      editMode={editMode}
                    />
                  </div>
                )}
                {showJackpotGuaranteeAmount && (
                  <>
                    <div className="text-sm text-gray-600 mt-1">
                      <InlineTranslationEdit
                        translationKey="chaseTheAce.actualJackpotAmount"
                        value={c('chaseTheAce.actualJackpotAmount')}
                        fallback="Actual Jackpot Amount"
                        onUpdate={handleTranslationUpdate}
                        editMode={editMode}
                      />
                      : ${actualJackpotAmount.toLocaleString()}
                    </div>
                    <div className="text-[11px] text-gray-400 italic mt-0.5 max-w-sm mx-auto" style={{ fontSize: '11px', fontStyle: 'italic', opacity: 0.82 }}>
                      <InlineTranslationEdit
                        translationKey="chaseTheAce.actualJackpotExplanation"
                        value={c('chaseTheAce.actualJackpotExplanation')}
                        fallback="The guaranteed jackpot is paid regardless of ticket sales. The actual jackpot shows the contribution amount generated from ticket sales so far."
                        onUpdate={handleTranslationUpdate}
                        editMode={editMode}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="text-xs text-gray-500 italic mb-3">
                <InlineTranslationEdit
                  translationKey="chaseTheAce.disclaimer"
                  value={c('chaseTheAce.disclaimer')}
                  fallback="Jackpot shown is an estimate and may be subject to adjustment."
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </div>

              {/* Spade divider */}
              <div className="flex items-center justify-center my-4">
                <div className="h-px bg-gray-300 flex-1" />
                <div className="mx-3 text-2xl text-gray-400">♠</div>
                <div className="h-px bg-gray-300 flex-1" />
              </div>

              {/* Weekly Prize */}
              <div className="mb-4">
                <div className="text-xs font-bold text-gray-900 uppercase tracking-widest mb-2">
                  <InlineTranslationEdit
                    translationKey="chaseTheAce.weeklyPrize"
                    value={c('chaseTheAce.weeklyPrize')}
                    fallback="Secondary Prize"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </div>
                <div className="text-3xl md:text-4xl font-bold text-gray-900">
                  ${weeklyPrize.toLocaleString()}
                </div>
                {showWeeklyGuarantee && (
                  <div className="text-xs text-gray-500 italic mt-2">
                    <InlineTranslationEdit
                      translationKey="chaseTheAce.guaranteeText"
                      value={c('chaseTheAce.guaranteeText')}
                      fallback="Guaranteed Prize"
                      onUpdate={handleTranslationUpdate}
                      editMode={editMode}
                    />
                  </div>
                )}
                {showWeeklyGuaranteeAmount && showWeeklyGuarantee && (
                  <div className="text-sm text-gray-600 mt-1">
                    <InlineTranslationEdit
                      translationKey="chaseTheAce.actualWeeklyAmount"
                      value={c('chaseTheAce.actualWeeklyAmount')}
                      fallback="Actual Weekly Amount"
                      onUpdate={handleTranslationUpdate}
                      editMode={editMode}
                    />
                    : ${actualWeeklyAmount.toLocaleString()}
                  </div>
                )}
              </div>

              {/* CTA Button */}
              {!salesClosed && (
                <button
                  onClick={() => purchaseNavigate('/chase-the-ace-purchase')}
                  className="inline-flex items-center justify-center gap-2 py-3 px-8 bg-charity-primary hover:bg-charity-primary-dark text-white font-bold text-lg rounded-full shadow-lg transition-all transform hover:scale-105 w-3/5 mx-auto"
                >
                  <InlineTranslationEdit
                    translationKey="chaseTheAce.playNow"
                    value={c('chaseTheAce.playNow')}
                    fallback="BUY NOW"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </button>
              )}
              {salesClosed && (
                <div className="inline-flex items-center justify-center gap-2 py-3 px-8 bg-gray-600 text-white font-bold text-lg rounded-full shadow-lg cursor-not-allowed opacity-75 w-3/5 mx-auto">
                  <InlineTranslationEdit
                    translationKey="chaseTheAce.salesClosed"
                    value={c('chaseTheAce.salesClosed')}
                    fallback="Sales Closed"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Dark sidebar */}
          <div className="lg:col-span-4 space-y-4">
            {/* Partner/Charity Logo */}
            {logoUrl && (
              <div className="bg-zinc-900 rounded-lg p-5 shadow-lg border border-charity-primary/30">
                <a href={orgWebsite} target="_blank" rel="noopener noreferrer" className="block">
                  <ResponsiveImage src={logoUrl} alt={logoAlt} className="h-20 w-full object-contain mb-4" sizes="240px" />
                </a>
                <p className="text-sm text-gray-400 text-center">
                  <InlineTranslationEdit
                    translationKey="chaseTheAce.charityMessage"
                    value={c('chaseTheAce.charityMessage')}
                    fallback="Will receive the net proceeds of this Chase the Ace draw"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
